/**
 * The face of a scene, behind the regions. The scene asks for one (SceneDefinition.avatar) and gives
 * its box; Settings, the theme and what is available decide which shows (avatar/FaceView.tsx). Without
 * MuseTalk the theme's stand-in shows: the stage's avatar idle, breathing, in the same place, or the orb
 * where the face would be. The three.js head is a whole-screen page.
 */
import { IdleFace, LiveFace, ParticlesFace, useFaceKind } from '../avatar/FaceView';
import { FACE } from '../modules/avatar/AvatarModule';
import { CANVAS } from '../layout/model';
import type { Box } from '../layout/geometry';
import type { SceneDefinition } from './scenes';
import css from './Shell.module.css';

const FULL_CANVAS: Box = { x: 0, y: 0, ...CANVAS };

/** The orb's square: centred on the face of the box, as wide as half the box. */
export function faceSpot(box: Box): Box {
  const side = Math.round(Math.min(box.width, box.height) * 0.5);
  const cx = box.x + box.width * FACE.cx;
  const cy = box.y + box.height * FACE.cy;
  return { x: Math.round(cx - side / 2), y: Math.round(cy - side / 2), width: side, height: side };
}

export function SceneFace({ scene }: { scene: SceneDefinition & { avatar: NonNullable<SceneDefinition['avatar']>; avatarBox: Box } }) {
  const { kind, standIn } = useFaceKind(scene.avatar);
  const box = kind === 'particles' ? faceSpot(scene.avatarBox) : kind === 'lowpoly' ? FULL_CANVAS : scene.avatarBox;
  // while MuseTalk connects: the theme's stand-in, where the face is (the orb on the face's spot)
  const spot = faceSpot(scene.avatarBox);
  const standInFace = standIn === 'idle' ? <IdleFace /> : (
    <div style={{ position: 'absolute', left: spot.x - box.x, top: spot.y - box.y, width: spot.width, height: spot.height }}><ParticlesFace /></div>
  );
  return (
    <div className={css.avatar} style={{ left: box.x, top: box.y, width: box.width, height: box.height, viewTransitionName: 'avatar' }} data-face={kind}>
      {kind === 'idle' ? <IdleFace />
        : kind === 'particles' ? <ParticlesFace />
        // until MuseTalk streams, the theme's stand-in takes its place (the three.js page: the station)
        : <LiveFace kind={kind} box={box} standIn={kind === 'musetalk' ? standInFace : null} />}
    </div>
  );
}
